(function () {
  'use strict';
  if (customElements.get('elmuno-gradient-personalizer')) return;
  const hashBlob = async blob => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())), value => value.toString(16).padStart(2, '0')).join('');
  customElements.define('elmuno-gradient-personalizer', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.config = window.ElmunoGradientConfig;
      this.core = window.ElmunoGradientOrderContract;
      this.artwork = window.ElmunoGradientArtwork;
      this.info = this.closest('product-info');
      this.form = this.closest('form[data-type="add-to-cart-form"]');
      try {
        this.local = JSON.parse(this.querySelector('[data-egp-config]').textContent);
        this.product = this.local.product;
        this.productConfig = this.config.products[String(this.product.productId)];
        this.previewRoot = this.querySelector('[data-egp-preview]');
        this.previewConfigNode = this.previewRoot.querySelector('[data-elmuno-preview-config]');
        this.previewConfig = JSON.parse(this.previewConfigNode.textContent);
        this.catalog = this.previewConfig.modelCatalog;
        if (this.config.version !== 1 || this.config.enabled !== true || this.productConfig?.approved !== true || !this.core || !this.artwork || !window.ElmunoProductPreview || !window.ElmunoModelSelection || !this.info || !this.form || !this.catalog || !this.config.fontUrl || !this.productConfig.backgroundUrl || String(this.productConfig.productId) !== String(this.product.productId)) return;
      } catch { return; }
      this.bound = true;
      this.destroyed = false;
      this.events = new AbortController();
      this.ticket = 0;
      this.mode = 'logo';
      this.composedName = '';
      this.typography = null;
      this.busy = false;
      this.statusKey = '';
      this.pending = null;
      this.saved = false;
      this.input = this.querySelector('[data-egp-name]');
      this.namePanel = this.querySelector('[data-egp-name-panel]');
      this.status = this.querySelector('[data-egp-status]');
      this.recovery = this.querySelector('[data-egp-recovery]');
      this.recheck = this.querySelector('[data-egp-recheck]');
      this.restart = this.querySelector('[data-egp-restart]');
      this.guard = this.info.querySelector('elmuno-gradient-model-guard');
      this.selectionKey = `elmuno-gradient-selection-v1:${this.productConfig.designKey}`;
      this.pendingKey = `elmuno-gradient-pending-v1:${this.product.productId}`;
      const options = { signal: this.events.signal };
      for (const radio of this.querySelectorAll('[data-egp-mode]')) radio.addEventListener('change', () => this.changeMode(radio.value), options);
      this.input.addEventListener('input', () => {
        if (this.busy || this.pending) return;
        this.statusKey = '';
        this.ticket++;
        this.composedName = '';
        this.persistSelection();
        clearTimeout(this.timer);
        this.refresh();
        this.timer = setTimeout(() => this.prepare(), 160);
      }, options);
      this.info.addEventListener('elmuno:gradient-model-change', () => this.modelChanged(), options);
      this.previewRoot.addEventListener('elmuno:preview-change', () => this.refresh(), options);
      this.form.addEventListener('submit', event => this.add(event), { ...options, capture: true });
      this.recheck.addEventListener('click', () => this.check(false), options);
      this.restart.addEventListener('click', () => this.check(true), options);
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.selectionKey));
        if (saved?.mode === 'name' && typeof saved.name === 'string') { this.mode = 'name'; this.input.value = saved.name; }
        const pending = JSON.parse(sessionStorage.getItem(this.pendingKey));
        if (this.core.validPending(pending) && String(pending.productId) === String(this.product.productId)) {
          this.pending = pending;
          this.mode = 'name';
          this.input.value = pending.handoff.Name;
          this.statusKey = 'uncertain';
        }
      } catch { }
      this.hidden = false;
      this.updateModeControls();
      this.modelChanged();
    }

    disconnectedCallback() {
      this.destroyed = true;
      this.ticket++;
      clearTimeout(this.timer);
      this.events?.abort();
      this.preview?.destroy();
      this.restoreExternalControls();
      this.bound = false;
    }

    message(key) { return this.local.messages[key] || ''; }

    normalizedName() {
      const canonical = this.core.normalizeName(this.input.value);
      if (!canonical) return null;
      const validation = this.artwork.validateName(canonical);
      return validation?.valid ? validation.name : null;
    }

    persistSelection() {
      try { sessionStorage.setItem(this.selectionKey, JSON.stringify({ mode: this.mode, name: this.input.value })); } catch { }
    }

    savePending(value) {
      if (value) sessionStorage.setItem(this.pendingKey, JSON.stringify(value));
      else sessionStorage.removeItem(this.pendingKey);
      this.pending = value;
    }

    updateModeControls() {
      for (const radio of this.querySelectorAll('[data-egp-mode]')) radio.checked = radio.value === this.mode;
      this.namePanel.hidden = this.mode !== 'name';
      this.previewRoot.hidden = this.mode !== 'name' || !this.entry;
    }

    changeMode(mode) {
      if (this.busy || this.pending || !['logo', 'name'].includes(mode)) return;
      this.mode = mode;
      this.ticket++;
      this.preparing = false;
      clearTimeout(this.timer);
      this.statusKey = '';
      this.updateModeControls();
      this.persistSelection();
      if (mode === 'name') { this.input.focus(); void this.prepare(); }
      else this.refresh();
    }

    modelChanged() {
      this.ticket++;
      this.entry = null;
      this.variant = this.guard?.currentVariant || null;
      if (this.variant) {
        const modelIndex = this.product.options.findIndex(option => (typeof option === 'string' ? option : option.name) === this.productConfig.modelOptionName);
        const orderModel = this.variant.options?.[modelIndex];
        this.identity = window.ElmunoModelSelection.choose(this.catalog, this.productConfig.caseType, orderModel);
        if (this.identity) this.entry = this.core.productionFor(this.config, this.product, this.variant, this.identity);
      }
      if (this.entry) {
        this.querySelector('[data-egp-order-model]').textContent = this.identity.orderModelLabel;
        this.querySelector('[data-egp-preview-model]').textContent = this.identity.previewModelLabel;
        this.querySelector('[data-egp-model-help]').textContent = this.message(this.identity.previewIsExample ? 'preview_example' : 'preview_exact');
      }
      this.updateModeControls();
      if (!this.busy && !this.pending && this.mode === 'name') void this.prepare();
      else this.refresh();
    }

    ensurePreview() {
      if (this.preview) return this.preview;
      this.previewConfig.defaultCaseType = this.productConfig.caseType;
      this.previewConfig.defaultModelKey = this.identity.previewModelKey;
      this.previewConfigNode.textContent = JSON.stringify(this.previewConfig);
      this.preview = window.ElmunoProductPreview.mount(this.previewRoot);
      if (!this.preview?.setArtwork) throw new Error('ELMUNO_NAME_PREVIEW_UNAVAILABLE');
      return this.preview;
    }

    async prepare() {
      const ticket = ++this.ticket;
      const name = this.normalizedName();
      this.composedName = '';
      this.preparing = false;
      if (this.destroyed || this.mode !== 'name' || this.busy || this.pending || !name || !this.entry) { this.refresh(); return; }
      const entry = this.entry;
      this.preparing = true;
      this.statusKey = '';
      this.refresh();
      try {
        const preview = this.ensurePreview();
        preview.selectModel(entry.caseType, this.identity.previewModelKey);
        const artwork = await this.artwork.compose({ name, backgroundUrl: this.productConfig.backgroundUrl, fontUrl: this.config.fontUrl });
        if (this.destroyed || ticket !== this.ticket || this.mode !== 'name' || this.entry !== entry) return;
        await preview.setArtwork(artwork.file);
        if (this.destroyed || ticket !== this.ticket || this.mode !== 'name' || this.entry !== entry) return;
        this.typography = artwork.typography;
        this.composedName = name;
      } catch (error) {
        if (!this.destroyed && ticket === this.ticket) {
          this.statusKey = 'preview_error';
          console.warn('Elmuno name preview:', error instanceof Error ? error.message : 'unknown');
        }
      } finally {
        if (!this.destroyed && ticket === this.ticket) { this.preparing = false; this.refresh(); }
      }
    }

    ready() {
      const name = this.normalizedName();
      const snapshot = this.preview?.snapshot();
      return Boolean(this.mode === 'name' && !this.busy && !this.pending && !this.saved && !this.preparing && this.entry && name && name === this.composedName && snapshot?.ready && snapshot.caseType === this.entry.caseType && snapshot.modelKey === this.identity.previewModelKey && String(this.variant?.id) === String(this.guard?.readyVariantId));
    }

    restoreExternalControls() {
      if (!this.externalControls) return;
      for (const [control, disabled] of this.externalControls) if (control.isConnected) control.disabled = disabled;
      this.externalControls = null;
    }

    refresh() {
      if (this.destroyed || !this.bound) return;
      const locked = Boolean(this.busy || this.pending || this.saved);
      this.querySelector('[data-egp-controls]').disabled = locked;
      if (locked && !this.externalControls) {
        this.externalControls = Array.from(this.info.querySelectorAll('[data-elmuno-model-select], input[name="quantity"]'), control => [control, control.disabled]);
        for (const [control] of this.externalControls) control.disabled = true;
      } else if (!locked) this.restoreExternalControls();
      this.recovery.hidden = !this.pending;
      this.recheck.disabled = this.busy;
      this.restart.disabled = this.busy;
      this.restart.hidden = this.recoverableId !== this.pending?.designId || !this.pending;
      const validName = this.normalizedName();
      this.dataset.egpStale = String(!validName || validName !== this.composedName || this.preparing);
      this.input.setAttribute('aria-invalid', String(this.mode === 'name' && Boolean(this.input.value.trim()) && !validName));
      this.blocksSubmit = this.mode === 'name' && !this.ready();
      let key = this.statusKey;
      if (!key && this.mode === 'name') key = !this.guard?.readyVariantId ? 'choose_model' : !this.entry ? 'unavailable' : !this.input.value.trim() ? 'enter_name' : !validName ? 'invalid_name' : !this.ready() ? 'loading_preview' : 'ready';
      if (this.mode === 'logo') key = '';
      this.submitLabel = this.blocksSubmit ? this.message(key) : '';
      this.status.textContent = this.message(key);
      this.status.dataset.error = String(['invalid_name', 'preview_error', 'save_failed', 'unavailable', 'invalid_quantity'].includes(key));
      this.guard?.refreshSubmit?.();
    }

    async request(path, options = {}, timeout = 15000) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.events.signal.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(abort, timeout);
      try {
        const response = await fetch(this.core.cartPath(this.local.rootUrl, path, location.origin), { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        let json = null;
        try { json = await response.json(); } catch { }
        return { response, json };
      } finally { clearTimeout(timer); this.events.signal.removeEventListener('abort', abort); }
    }

    async reconcile(expected) {
      const { response, json } = await this.request('cart.js', { headers: { Accept: 'application/json' } });
      return response.ok ? this.core.reconcileCart(json, expected, location.origin) : 'unknown';
    }

    complete() {
      try { this.savePending(null); } catch { this.pending = null; }
      this.saved = true;
      this.statusKey = 'saved';
      this.refresh();
      location.assign(this.local.cartUrl);
    }

    async check(restart) {
      if (this.busy || !this.pending || (restart && this.recoverableId !== this.pending.designId)) return;
      const expected = this.pending;
      this.busy = true;
      this.recoverableId = null;
      this.statusKey = 'checking';
      this.refresh();
      try {
        const result = await this.reconcile(expected);
        if (this.destroyed || this.pending !== expected) return;
        if (result === 'confirmed') this.complete();
        else if (result === 'absent' && restart) { this.savePending(null); this.statusKey = ''; }
        else if (result === 'absent') { this.recoverableId = expected.designId; this.statusKey = 'absent'; }
        else this.statusKey = 'uncertain';
      } catch { if (!this.destroyed) this.statusKey = 'uncertain'; }
      finally {
        if (!this.destroyed) {
          this.busy = false;
          this.refresh();
          if (!this.pending && !this.saved && restart) void this.prepare();
        }
      }
    }

    assertFresh(snapshot, variant, entry, name, ticket) {
      const current = this.preview.snapshot();
      if (this.destroyed || this.ticket !== ticket || this.mode !== 'name' || this.variant !== variant || this.entry !== entry || this.normalizedName() !== name || current.revision !== snapshot.revision || current.file !== snapshot.file || String(this.guard.readyVariantId) !== String(variant.id)) throw new Error('ELMUNO_STALE_DESIGN');
    }

    async add(event) {
      if (this.mode !== 'name') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.ready()) { this.refresh(); this.input.focus(); return; }
      const quantity = Number(new FormData(this.form).get('quantity') || 1);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) { this.statusKey = 'invalid_quantity'; this.refresh(); return; }
      const entry = this.entry;
      const variant = this.variant;
      const name = this.normalizedName();
      const snapshot = this.preview.snapshot();
      const ticket = this.ticket;
      let dispatched = false;
      let definiteFailure = false;
      this.busy = true;
      this.statusKey = 'exporting';
      this.refresh();
      try {
        const print = await this.preview.exportPrint(entry.template);
        this.assertFresh(snapshot, variant, entry, name, ticket);
        const mockup = await this.preview.exportMockup();
        this.assertFresh(snapshot, variant, entry, name, ticket);
        if (mockup.snapshot.revision !== snapshot.revision || mockup.snapshot.file !== snapshot.file) throw new Error('ELMUNO_STALE_DESIGN');
        const [printHash, previewHash] = await Promise.all([hashBlob(print.blob), hashBlob(mockup.blob)]);
        this.assertFresh(snapshot, variant, entry, name, ticket);
        const designId = crypto.randomUUID();
        const handoff = {
          ...this.core.handoffProperties(entry),
          Personalisierung: 'Eigener Name', Name: name, Design: entry.designName,
          _elmuno_personalization: 'name', _elmuno_name: name,
          _elmuno_font: this.config.fontFamily || 'Elmuno Script', _elmuno_artwork_version: this.config.artworkVersion || '1'
        };
        const expected = { productId: String(this.product.productId), variantId: String(variant.id), designId, printHash, previewHash, templateId: entry.template.id, handoff, fileVersion: 2, quantity };
        const data = new FormData(this.form);
        data.set('id', expected.variantId);
        data.set('product-id', expected.productId);
        data.set('quantity', String(quantity));
        data.set('properties[_elmuno_original]', snapshot.file, `elmuno-${designId}-original.png`);
        data.set('properties[_elmuno_print]', new File([print.blob], `elmuno-${designId}-2000x3400.png`, { type: 'image/png' }));
        data.set('properties[_elmuno_preview]', new File([mockup.blob], `elmuno-${designId}-vorschau.png`, { type: 'image/png' }));
        const metadata = { ...handoff, _elmuno_design_id: designId, _elmuno_file_version: '2', _elmuno_print_sha256: printHash, _elmuno_preview_sha256: previewHash, _elmuno_template: entry.template.id, _elmuno_variant: expected.variantId, _elmuno_typography: JSON.stringify(this.typography) };
        for (const [key, value] of Object.entries(metadata)) data.set(`properties[${key}]`, value);
        this.assertFresh(snapshot, variant, entry, name, ticket);
        this.savePending(expected);
        this.statusKey = 'uploading';
        this.refresh();
        dispatched = true;
        const { response, json } = await this.request('cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data }, 90000);
        if (!response.ok) { definiteFailure = [400, 404, 422].includes(response.status); throw new Error('ELMUNO_ADD_FAILED'); }
        const items = Array.isArray(json?.items) ? json.items : [json];
        if (!items.some(item => this.core.confirmedItem(item, expected, location.origin))) throw new Error('ELMUNO_FILES_UNCONFIRMED');
        this.complete();
      } catch {
        if (this.destroyed) return;
        if (dispatched && !definiteFailure) {
          try { if (await this.reconcile(this.pending) === 'confirmed' && !this.destroyed) { this.complete(); return; } } catch { }
          this.statusKey = 'uncertain';
        } else {
          try { this.savePending(null); } catch { this.pending = null; }
          this.statusKey = 'save_failed';
        }
      } finally {
        if (!this.destroyed) { this.busy = false; this.refresh(); }
      }
    }
  });
})();
